import {
  IVendorOrderCreateResponse,
  IVendorOrderListItem
} from '@/api/vendorApplicationsApi';
import { IErrorData } from '@/utils/getErrorData';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  createVendorOrderThunk,
  getVendorOrdersThunk,
  getVendorOrderThunk
} from './vendorApplicationsThunks';
import { updateVendorOrderOfferStatusSharedThunk } from '../shared/vendorOrderOfferThunks';
import { toast } from 'react-toastify';

export interface IVendorApplicationsState {
  createdOrder: IVendorOrderCreateResponse | null;
  isCreating: boolean;
  error: IErrorData | null;
  orders: IVendorOrderListItem[];
  totalOrders: number;
  isLoadingOrders: boolean;
  ordersError: IErrorData | null;
  currentOrder: IVendorOrderCreateResponse | null;
  isLoadingOrder: boolean;
  orderError: IErrorData | null;
  updatingOfferStatuses: Record<string, boolean>;
}

const initialState: IVendorApplicationsState = {
  createdOrder: null,
  isCreating: false,
  error: null,
  orders: [],
  totalOrders: 0,
  isLoadingOrders: false,
  ordersError: null,
  currentOrder: null,
  isLoadingOrder: false,
  orderError: null,
  updatingOfferStatuses: {}
};

const vendorApplicationsSlice = createSlice({
  name: 'vendorApplications',
  initialState,
  reducers: {
    resetVendorApplications(state) {
      state.createdOrder = initialState.createdOrder;
      state.isCreating = initialState.isCreating;
      state.error = initialState.error;
      state.orders = initialState.orders;
      state.totalOrders = initialState.totalOrders;
      state.isLoadingOrders = initialState.isLoadingOrders;
      state.ordersError = initialState.ordersError;
      state.currentOrder = initialState.currentOrder;
      state.isLoadingOrder = initialState.isLoadingOrder;
      state.orderError = initialState.orderError;
      state.updatingOfferStatuses = initialState.updatingOfferStatuses;
    },
    setCreatedOrder(
      state,
      action: PayloadAction<IVendorOrderCreateResponse | null>
    ) {
      state.createdOrder = action.payload;
    },
    resetOrders(state) {
      state.orders = [];
      state.totalOrders = 0;
      state.ordersError = null;
      state.isLoadingOrders = false;
    },
    resetCurrentOrder(state) {
      state.currentOrder = null;
      state.orderError = null;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(createVendorOrderThunk.fulfilled, (state, action) => {
      state.createdOrder = action.payload;
      state.isCreating = false;
      state.error = null;
      toast.success('Заявка для поставщика успешно создана');
    });

    builder.addCase(createVendorOrderThunk.pending, (state) => {
      state.error = null;
      state.isCreating = true;
    });

    builder.addCase(createVendorOrderThunk.rejected, (state, action) => {
      state.error = action.payload ?? {
        message: 'Произошла необработанная ошибка при создании заявки'
      };
      state.isCreating = false;
    });

    builder.addCase(getVendorOrdersThunk.fulfilled, (state, action) => {
      state.orders = action.payload.items;
      state.totalOrders = action.payload.meta.total;
      state.isLoadingOrders = false;
      state.ordersError = null;
    });

    builder.addCase(getVendorOrdersThunk.pending, (state) => {
      state.ordersError = null;
      state.isLoadingOrders = true;
    });

    builder.addCase(getVendorOrdersThunk.rejected, (state, action) => {
      state.ordersError = action.payload ?? {
        message: 'Произошла необработанная ошибка при получении списка заказов'
      };
      state.isLoadingOrders = false;
    });

    builder.addCase(getVendorOrderThunk.fulfilled, (state, action) => {
      state.currentOrder = action.payload;
      state.isLoadingOrder = false;
      state.orderError = null;
    });

    builder.addCase(getVendorOrderThunk.pending, (state) => {
      state.orderError = null;
      state.isLoadingOrder = true;
    });

    builder.addCase(getVendorOrderThunk.rejected, (state, action) => {
      state.orderError = action.payload ?? {
        message: 'Произошла необработанная ошибка при получении данных заказа'
      };
      state.isLoadingOrder = false;
    });

    builder.addCase(
      updateVendorOrderOfferStatusSharedThunk.pending,
      (state, action) => {
        const { order_offer_uuid } = action.meta.arg;
        state.updatingOfferStatuses[order_offer_uuid] = true;
      }
    );

    builder.addCase(
      updateVendorOrderOfferStatusSharedThunk.fulfilled,
      (state, action) => {
        const { order_offer_uuid } = action.meta.arg;
        state.updatingOfferStatuses[order_offer_uuid] = false;

        if (state.currentOrder?.vendorOrderOffers != null) {
          const offerIndex = state.currentOrder.vendorOrderOffers.findIndex(
            (offer) => offer.id === order_offer_uuid
          );
          if (offerIndex !== -1) {
            state.currentOrder.vendorOrderOffers[offerIndex].status =
              action.payload.status;
          }
        }
      }
    );

    builder.addCase(
      updateVendorOrderOfferStatusSharedThunk.rejected,
      (state, action) => {
        const { order_offer_uuid } = action.meta.arg;
        state.updatingOfferStatuses[order_offer_uuid] = false;
      }
    );
  }
});

export const {
  resetVendorApplications,
  setCreatedOrder,
  resetOrders,
  resetCurrentOrder
} = vendorApplicationsSlice.actions;
export default vendorApplicationsSlice.reducer;
